import React, {
  forwardRef,
  Fragment,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import cs from "classnames";

import type {
  ButtonSelectOption,
  ButtonSelectProps,
  OptionTileButtonProps,
  Variants,
} from "@braintrust/braintrust-ui-components";
import {
  Box,
  FormHelperText,
  OptionTileButton,
  Tooltip,
  useButtonSelect,
} from "@braintrust/braintrust-ui-components";
import { CheckCircleFilledIcon } from "@braintrust/braintrust-ui-components/Icons";
import { isNotNullable } from "@js/utils";

import styles from "./style.module.scss";

export type OptionTileButtonsOption<T = string> = ButtonSelectOption<T> & {
  tooltipTitle?: string;
  text?: string;
  disabledTooltipTitle?: string;
};

export type OptionTileButtonsProps<
  TValue = string,
  TOption extends OptionTileButtonsOption<TValue> =
    OptionTileButtonsOption<TValue>,
> = ButtonSelectProps<TValue, false, TOption> & {
  renderCustomOptionButton?: RenderCustomOptionButtonFun;
  className?: string;
  buttonClassName?: string;
  hideError?: boolean;
  selectedClassName?: string;
  variant?: EnumType<typeof Variants>;
  selectedVariant?: EnumType<typeof Variants>;
  disabled?: boolean;
  renderOptionButton?: RenderOptionButtonFun<TOption>;
  showCheckIcon?: boolean;
};

type RenderOptionButtonArgs<TOption> = {
  defaultElement: React.ReactElement;
  option: TOption;
  disabled: boolean;
};
type RenderOptionButtonFun<TOption> = (
  args: RenderOptionButtonArgs<TOption>,
) => ReactNode;

export type RenderCustomOptionButtonArgs = Pick<
  ReturnType<typeof useButtonSelect>,
  "itemA11yProps" | "handleChange"
> & {
  isSelected: boolean;
  onClick: () => void;
  setActiveIndex: (index: number) => void;
};

type RenderCustomOptionButtonFun = (
  args: RenderCustomOptionButtonArgs,
) => ReactNode;

export const OptionTileButtons = <TValue = string,>({
  className,
  buttonClassName,
  value,
  onChange,
  error,
  hideError = false,
  options,
  selectedClassName,
  renderCustomOptionButton,
  canSingleValueBeEmpty,
  variant = "white-violet",
  selectedVariant = "violet",
  disabled,
  renderOptionButton,
  showCheckIcon,
}: OptionTileButtonsProps<TValue>) => {
  const { itemA11yProps, containerA11yProps, handleChange, isSelected } =
    useButtonSelect<TValue>({
      value,
      onChange,
      canSingleValueBeEmpty,
    });

  const [activeIndex, setActiveIndex] = useState<number | undefined>(() => {
    const indexOfPreselectedOption = options.findIndex((option) =>
      isSelected(option),
    );
    return indexOfPreselectedOption === -1
      ? undefined
      : indexOfPreselectedOption;
  });
  const [isCustomOptionActive, setIsCustomOptionActive] =
    useState<boolean>(false);

  const showError = error && !hideError;

  useEffect(() => {
    if ((!isNotNullable(value) || value === "") && !isCustomOptionActive) {
      setActiveIndex(undefined);
    }
  }, [isCustomOptionActive, value]);

  return (
    <Box>
      <div
        className={cs(styles.buttonsWrapper, className)}
        {...containerA11yProps}
      >
        {options.map((option, index) => {
          const isCurrentOptionSelected =
            activeIndex === index ||
            (isSelected(option) && !isCustomOptionActive);
          const currentOptionDisabled = !!(option.disabled !== undefined
            ? option.disabled
            : disabled);

          const key = JSON.stringify(option.value);
          const button = (
            <CustomOptionTileButton
              key={key}
              {...itemA11yProps(isCurrentOptionSelected)}
              option={{
                value: option.value,
                label: (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {isCurrentOptionSelected && showCheckIcon && (
                      <Box sx={{ mr: 1 }}>
                        <CheckCircleFilledIcon fontSize="medium" />
                      </Box>
                    )}
                    {option.label}{" "}
                    {/* Assuming `option` has a `label` property */}
                  </Box>
                ),
              }}
              isSelected={isCurrentOptionSelected}
              disabled={currentOptionDisabled}
              selectedClassName={selectedClassName}
              variant={isCurrentOptionSelected ? selectedVariant : variant}
              className={buttonClassName}
              handleChange={(val) => {
                setActiveIndex((prevActiveIndex) => {
                  return prevActiveIndex === index ? undefined : index;
                });
                handleChange(val);
                setIsCustomOptionActive(false);
              }}
            />
          );

          if (renderOptionButton) {
            return (
              <Fragment key={key}>
                {renderOptionButton({
                  defaultElement: button,
                  option,
                  disabled: currentOptionDisabled,
                })}
              </Fragment>
            );
          }

          if (
            option.tooltipTitle ||
            (option.disabledTooltipTitle && option.disabled)
          ) {
            return (
              <Tooltip
                key={key}
                title={option.tooltipTitle || option.disabledTooltipTitle || ""}
                placement="top"
              >
                <span>{button}</span>
              </Tooltip>
            );
          }

          return <div key={key}>{button}</div>;
        })}
        {renderCustomOptionButton &&
          renderCustomOptionButton({
            isSelected: activeIndex === options.length,
            onClick: () => {
              setActiveIndex(options.length);
              setIsCustomOptionActive(true);
            },
            setActiveIndex,
            itemA11yProps,
            handleChange,
          })}
      </div>

      {showError && (
        <Box sx={{ mt: 1 }}>
          <FormHelperText error>{error}</FormHelperText>
        </Box>
      )}
    </Box>
  );
};

export const CustomOptionTileButton = forwardRef<
  HTMLButtonElement,
  OptionTileButtonProps
>(
  (
    {
      option,
      handleChange,
      isSelected,
      disabled,
      variant,
      className,
      ...props
    },
    ref,
  ) => {
    const Icon = option.icon;
    return (
      <OptionTileButton
        className={className}
        handleChange={handleChange}
        ref={ref}
        option={{
          ...option,
          label: (
            <CustomOptionTileButtonLabelContainer>
              {!!Icon && (
                <Box sx={{ mr: 0.5, mb: 0.5 }}>
                  <Icon />
                </Box>
              )}
              {option.label}
            </CustomOptionTileButtonLabelContainer>
          ),
        }}
        isSelected={isSelected}
        variant={variant || "white-violet"}
        disabled={disabled}
        {...props}
      />
    );
  },
) as <TValue = string>(
  p: OptionTileButtonProps<TValue> & { ref?: React.Ref<HTMLButtonElement> },
) => ReactNode;

const CustomOptionTileButtonLabelContainer = ({
  children,
}: {
  children: ReactNode;
}) => {
  return (
    <Box
      className="option-tile-label"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {children}
    </Box>
  );
};
